import React from 'react';
import { VendorSelect } from '../../components/VendorSelect';
import type { ClientInvoiceItemDto } from '../../lib/clientInvoiceApi';
import { DraftInput } from './DraftInput';
import { num } from '../../utils/invoiceLineMath';
import { sellingPriceLabel, costPriceLabel } from './linePriceLabel';
import { DELIVERY_FIELD_NOTES } from './deliveryFieldNotes';
import type { CostEntry } from './lineCostCurrency';
import type { QuoteInfo } from './quoteInfo';
import { StockItemPicker } from './StockItemPicker';
import { descriptionDiffersFromName } from './lineText';
import { WritebackForm } from './WritebackForm';
import type { WritebackAnswers } from './worksheetPrompts';
import { costSourceLabel } from './costSourceLabels';
import { CostSourceTag } from './CostSourceTag';
import { readsAsInventory, INVENTORY_SUPPLIER } from './inventorySupplier';
import { caseConversionOf } from './caseConversion';
import { isPersonSetCost, isMarkedNotSupplied } from '../../utils/clientInvoiceLine';
import type { SlipRowForLine } from '../../utils/clientInvoiceDispatch';
import { formatDate } from '../../utils/dateFormat';
import { formatQty } from '../../utils/unitConversions';

/**
 * D98.1: everything about one invoice line that is not in the slim grid. Presentational: the page
 * passes its EXISTING handlers, this file does no money maths and fetches nothing.
 *
 * Handlers (all keyed by the line's `index`, as the page's own functions are):
 *   updateItem(index, patch)              page updateItem
 *   onItemNameChange(index, text)         page handleRowItemNameChange
 *   updateCostPrice / updateMarkup / updateSellingPrice(index, text)
 *   updateTtdCost(index, text)            the cost typed in TT$; the entry box when pricing.costEntry is 'ttd' (D152.2)
 *   applyUomChange(index, nextUnit)
 *   onUseSystemCost(index)                page updateItemPricing(index, { costBasis: null })
 *   removeItem(index)                     the page asks (an order line is kept as Not supplied, D170.7)
 *   Restore line                          updateItem(index, { notSuppliedReason: '' }) on a line marked Not supplied
 *   onSourceQtyChange(field, value)       the page derives the total it implies - NOT done here.
 *   onWriteback(answers)                  page writebackProcurementPlanLine call
 * Display values the page already owns arrive ready-made: `pricing` (texts from getRowPricing),
 * `quoted` (the order line's unit, price and remark, D152.3), `totalBilled`, `remainingDispatch`, `money`,
 * `poMatchQuantity`, `removalLock` (lineRemovalLock), `supplierBilled` (D171.4).
 * Locks: `readOnly` = cancelled (D96.18) disables everything; `linesLocked` = sent (D96.2) disables
 * quantities, unit, prices and item text; cost, supplier, remarks, cold flag and status colour stay editable.
 */
export type DrawerIssue = { severity: 'error' | 'warning'; message: string };
export type SourceQtyField = 'pantry' | 'stock' | 'vendor0' | 'vendor1' | 'vendor2';
export type DrawerPricing = {
  costText: string;
  sellingPriceText: string;
  markupText: string;
  /** D119.15: set only while `markupText` is a typed rate that the sale realises differently (>= 0.01): the per-line actual %. */
  makesMarkupPercent?: number | null;
  /** Mark-up needs a cost above zero; without one the field is blank and read-only. */
  hasCost: boolean;
  markupAmount: number | null;
  lineTotal: number | null;
  /**
   * D152.2: where this line's cost is typed (the page decides, `costEntryFor`). 'ttd' = the TT$ box is the entry and
   * the US$ cost below it is read-only text; 'single' = the one cost box in the invoice's own currency.
   */
  costEntry: CostEntry;
  /**
   * D119.9: the TT$ cost, only on a US$ invoice (omit it on TT$ / no currency). `text` is the cost stated in
   * TT$ at the page's rate. `disabledReason` set = no usable rate (so `costEntry` is 'single'): the box is off and says why.
   */
  ttdCost?: { text: string; disabledReason: string | null };
  /**
   * The cost the PO gave, in TT$ (2 places), while the bill's cost differs from it: shown small, grey and struck through
   * above the TT$ cost box. Null/omitted = no change to show (no bill, or the same cost).
   */
  ttdCostBefore?: string | null;
};

type Props = {
  /** Viewport y where the panel starts (the bottom of the sticky invoice header); 64 when unmeasured. */
  topOffset?: number;
  item: ClientInvoiceItemDto;
  index: number;
  total: number;
  currency: string;
  issues: DrawerIssue[];
  readOnly: boolean;
  linesLocked: boolean;
  removalLock: string | null;
  pricing: DrawerPricing;
  /** D152.3: what the client was quoted on this line, ready-made by the page (`quoteInfoFor`); null/omitted = no section. */
  quoted?: QuoteInfo;
  totalBilled: number;
  /** D171.4: what the supplier billed for the line (its `supplierBilledQty`), ready-made by the page; null/omitted = no "Bill X" offer. */
  supplierBilled?: number | null;
  /** D172.5: which side of what the line buys `supplierBilled` sits on ('short' | 'over'); omitted/null with a figure = 'short' (a line stored before D172.5). */
  supplierBilledVs?: 'short' | 'over' | null;
  remainingDispatch: number;
  /** D158.3: what each delivery slip shipped of this line (`slipRowsForLine`), ready-made by the page; empty/omitted = nothing shown. */
  slipHistory?: ReadonlyArray<SlipRowForLine>;
  money: (value: number | null | undefined, currency?: string) => string;
  poMatchQuantity: (match: { quantity?: number; uom?: string }) => string;
  writebackBusy?: boolean;
  /** Invoices - Edit: without it the "Record actual pick" button is not offered (default true). */
  canWriteback?: boolean;
  /** Ordered but billed 0 (lineIssues' not_supplied): shows the "Not supplied reason" field. */
  notSupplied?: boolean;
  /** Item-name suggestions (page-owned) shown under the Name field; focus tells the page which line is typing. */
  nameExtra?: React.ReactNode;
  onItemNameFocus?: () => void;
  /** `typing` = true from a per-keystroke text field (its burst is one undo step, D119.11); a tick, pick or click passes nothing. */
  updateItem: (index: number, patch: Partial<ClientInvoiceItemDto>, typing?: boolean) => void;
  onItemNameChange: (index: number, text: string) => void;
  updateCostPrice: (index: number, text: string) => void;
  /** D119.9 / D152.2: a cost typed in TT$; the page converts it and writes it like a typed Cost. */
  updateTtdCost: (index: number, text: string) => void;
  updateMarkup: (index: number, text: string) => void;
  updateSellingPrice: (index: number, text: string) => void;
  applyUomChange: (index: number, next: string) => void;
  onUseSystemCost: (index: number) => void;
  removeItem: (index: number) => void;
  onSourceQtyChange: (field: SourceQtyField, value: number) => void;
  onWriteback: (answers: WritebackAnswers) => void;
  /** D119.11: undo / redo of the page's unsaved edits (every line, not just this one). Omit `onUndo` to hide the pair. */
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

// The five line status colours (the three unused page entries are dropped, D98.4).
export const STATUS_OPTIONS: Array<{ value: NonNullable<ClientInvoiceItemDto['itemStatus']>; label: string }> = [
  { value: '', label: 'No status' },
  { value: 'red', label: 'Problem' },
  { value: 'purple', label: 'Break case' },
  { value: 'pink', label: 'Ready' },
  { value: 'orange', label: 'Outstanding (supplier)' },
  { value: 'blue', label: 'Outstanding (retail)' },
];

const SOURCE_LABELS: Record<string, string> = {
  confirmed_order: 'Confirmed order',
  manual: 'Manual',
  inventory: 'Inventory',
  catalogue: 'Catalogue',
  supplier: 'Supplier',
  pantry: 'Pantry',
};
const CODE_MEANING: Record<string, string> = { O: 'Order (vendor)', S: 'Stock', P: 'Pantry' };

const qty = (v: unknown) => num(v) ?? 0;
const inputCls = 'h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500';
const areaCls = 'w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100 disabled:text-slate-500';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 border-b border-slate-200 px-4 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

/** `hint`: an always-visible grey sentence under the value, its own grid cell so the value cell stays the bare value. */
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-start gap-2 text-sm">
      <span className="pt-1.5 text-xs text-slate-500">{label}</span>
      <div className="min-w-0">{children}</div>
      {hint && <div className="col-start-2 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export function LineDrawer(p: Props) {
  const { item, index } = p;
  const lock = p.readOnly || p.linesLocked; // quantities, unit, prices, item text
  const soft = p.readOnly; // cost, supplier, remarks, cold flag, status colour (D96.2)
  const marked = isMarkedNotSupplied(item); // D170.7: no source quantity until Restore line
  const markedTitle = marked ? DELIVERY_FIELD_NOTES.notSupplied : undefined;
  const [descOpen, setDescOpen] = React.useState(false);
  const [writebackOpen, setWritebackOpen] = React.useState(false);
  React.useEffect(() => {
    setDescOpen(false);
    setWritebackOpen(false);
  }, [index]);
  const { onClose } = p;
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sameAsName = !descriptionDiffersFromName(item.itemName, item.itemDescription);
  const showDescription = !sameAsName || descOpen;
  const vendors = item.vendorQuantities || [];
  const codes = [
    ...(qty(item.qtyFromPantry) > 0 ? ['P'] : []),
    ...(qty(item.qtyFromStock) > 0 ? ['S'] : []),
    ...(vendors.some((v) => qty(v?.quantity) > 0) ? ['O'] : []),
  ];
  const provenance = (item.sourceProvenance || []).filter((x) => qty(x?.quantity) > 0);
  // D171.4: one click bills what the supplier billed, while the line bills MORE than a SHORT figure (a damaged case billed less
  // stays); D172.5 (29 Oct call, Deborah): or LESS than an OVER figure (a whole case for a broken one; billed more stays). With
  // source boxes it sets the ONE vendor row on the PO's supplier (the line's one match), none = no offer; else the
  // billed quantity. Nothing changes without the click (D145).
  const billed = p.supplierBilled;
  const billedOver = p.supplierBilledVs === 'over';
  const sourced = codes.length > 0;
  const poVendorId = item.poMatches?.length === 1 ? item.poMatches[0].vendorId : null;
  const poRows = poVendorId ? vendors.flatMap((v, slot) => (v?.vendorId === poVendorId ? [slot] : [])) : [];
  const supplierSlot = poRows.length === 1 && poRows[0] < 3 ? poRows[0] : null; // the drawer's three Vendor boxes
  const billingNow = !sourced ? p.totalBilled : supplierSlot != null ? qty(vendors[supplierSlot].quantity) : null;
  const billSupplier = billed != null && !lock && !marked && billingNow != null && (billedOver ? billingNow < billed : billingNow > billed) && (
    <button
      type="button"
      onClick={() => (sourced && supplierSlot != null ? p.onSourceQtyChange(`vendor${supplierSlot}` as SourceQtyField, billed) : p.updateItem(index, { invoiceQuantity: billed }))}
      title="Bill the quantity the supplier billed. Check what was delivered first; save to keep it."
      className="rounded border border-amber-300 bg-white px-2 py-1 text-xs font-semibold text-amber-800"
    >
      {`Bill ${formatQty(billed)} ${item.billUnit || item.unit || ''}`.trim()}
    </button>
  );
  // A person's cost on an ORDER line can go back to the system's; a hand line has no system cost (D157.9).
  const personCostOnOrderLine = isPersonSetCost(item.costBasis) && item.confirmedOrderItemIndex != null;
  const costFrom = costSourceLabel(item);
  const conversion = caseConversionOf(item);
  // The old TT$ cost, struck through, above the box that states the TT$ cost.
  const costBefore = p.pricing.ttdCostBefore ? (
    <div aria-label="Cost before" title="The cost on the purchase order, before the bill changed it" className="mb-0.5 text-xs text-slate-400 line-through">
      {p.pricing.ttdCostBefore}
    </div>
  ) : null;
  const vendorList = (slot: number, patch: Record<string, unknown>) => {
    const list = [...vendors];
    while (list.length <= slot) list.push({ vendorName: '', quantity: 0 });
    list[slot] = { ...list[slot], ...patch };
    return list;
  };
  const invoiceUnit = item.billUnit || item.packingUnit || item.unit || '';
  const priceLabel = sellingPriceLabel(p.currency);
  // D161.7: a highlight is drawn only while its box is enabled; a disabled box keeps the plain disabled look.
  const overrideCls = (disabled: boolean) => (item.uomOverridden && !disabled ? 'border-2 border-emerald-400 bg-emerald-50' : '');
  const markupDisabled = lock || !p.pricing.hasCost;
  const ttdEntry = p.pricing.costEntry === 'ttd';
  const costLabel = costPriceLabel(p.currency);
  const ttdLabel = costPriceLabel('TTD');
  // D119.12: the pack this cost was quoted on, stored at order build; shown in Quote information (D152.3).
  // Internal: on no PDF and not in the CSV.
  const quotedPack = item.quotedPack && (
    <div role="note" aria-label="Quoted pack" className="mt-1 text-xs text-slate-500">
      Quoted pack: {item.quotedPack}
    </div>
  );
  // "Use system cost" sits under the box a person types the cost in; "Cost from" says whose cost it is.
  const useSystemCost = personCostOnOrderLine && (
    <div className="mt-1 flex items-center gap-2 text-xs">
      <button
        type="button"
        disabled={soft}
        onClick={() => p.onUseSystemCost(index)}
        className="text-sky-700 underline disabled:opacity-40"
        title="Hand the cost back to the system: the shipped stock cost, else the supplier's bill, else the PO price (for a line filled from stock, the order's own cost). Saves the invoice now, so the restored cost shows."
      >
        Use system cost
      </button>
    </div>
  );

  return (
    <aside aria-label="Line detail" style={{ top: p.topOffset ?? 64 }} className="fixed bottom-0 right-0 z-40 flex w-[440px] flex-col overflow-y-auto border-l border-slate-200 bg-white text-sm shadow-2xl">
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <b className="text-base">
          Line {index + 1} of {p.total}
        </b>
        <span className="flex-1" />
        {p.onUndo && (
          <>
            <button
              type="button"
              aria-label="Undo"
              title="Undo the last change you made on this invoice (Ctrl+Z)"
              disabled={!p.canUndo}
              onClick={p.onUndo}
              className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
            >
              Undo
            </button>
            <button
              type="button"
              aria-label="Redo"
              title="Put back the change you just undid (Ctrl+Y)"
              disabled={!p.canRedo}
              onClick={p.onRedo}
              className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
            >
              Redo
            </button>
          </>
        )}
        <button type="button" aria-label="Previous line" disabled={index <= 0} onClick={p.onPrev} className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">
          Previous
        </button>
        <button type="button" aria-label="Next line" disabled={index >= p.total - 1} onClick={p.onNext} className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40">
          Next
        </button>
        <button type="button" aria-label="Close" onClick={p.onClose} className="rounded border border-slate-300 px-2 py-1">
          Close
        </button>
      </header>

      {(p.issues.length > 0 || billSupplier) && (
        <section aria-label="Issues for this line" className="space-y-1 border-b border-slate-200 px-4 py-3">
          {p.issues.map((i, k) => (
            <div key={k} className={`rounded px-2 py-1 text-xs ${i.severity === 'error' ? 'bg-rose-50 text-rose-800' : 'bg-amber-50 text-amber-800'}`}>
              {i.message}
            </div>
          ))}
          {billSupplier}
        </section>
      )}

      <Section title="Item - prints on the invoice">
        <Row label="Name">
          <input aria-label="Item name" disabled={lock} value={item.itemName || ''} onFocus={p.onItemNameFocus} onChange={(e) => p.onItemNameChange(index, e.target.value)} className={inputCls} />
          {p.nameExtra}
        </Row>
        <Row label="Description">
          {showDescription ? (
            <input
              aria-label="Description"
              disabled={lock}
              value={item.itemDescription || ''}
              onChange={(e) => p.updateItem(index, { itemDescription: e.target.value, descriptionAmended: true }, true)}
              className={inputCls}
            />
          ) : (
            <button type="button" disabled={lock} onClick={() => setDescOpen(true)} className="text-xs text-sky-700 underline disabled:opacity-40">
              Add description
            </button>
          )}
        </Row>
        <Row label="Packing details (prints)">
          <input
            aria-label="Packing details"
            disabled={lock}
            value={item.itemPackingDetails || ''}
            onChange={(e) => p.updateItem(index, { itemPackingDetails: e.target.value }, true)}
            placeholder="Brand / pack size / cases"
            className={inputCls}
          />
        </Row>
        <Row label="Client remark (prints under the item)">
          <textarea aria-label="Client remark" disabled={soft} rows={2} value={item.clientRemarks || ''} onChange={(e) => p.updateItem(index, { clientRemarks: e.target.value }, true)} className={areaCls} />
        </Row>
      </Section>

      <Section title="Quantity and sourcing">
        <Row label="Ordered">
          {item.confirmedOrderItemIndex != null ? (
            <span aria-label="Confirmed quantity">
              {item.confirmedQuantity ?? 0} {item.unit || ''} <span className="text-xs text-slate-500">(from the order)</span>
            </span>
          ) : (
            <div className="flex gap-2">
              <input
                aria-label="Confirmed quantity"
                type="number"
                min="0"
                disabled={lock}
                value={item.confirmedQuantity || ''}
                onChange={(e) => p.updateItem(index, { confirmedQuantity: qty(e.target.value) }, true)}
                className={inputCls}
              />
              <input aria-label="Confirmed unit" disabled={lock} value={item.unit || ''} onChange={(e) => p.updateItem(index, { unit: e.target.value }, true)} className={inputCls} />
            </div>
          )}
        </Row>
        <Row label="Invoice unit">
          <DraftInput
            aria-label="Invoice unit"
            disabled={lock}
            value={invoiceUnit}
            onCommit={(next) => p.applyUomChange(index, next)}
            title={
              item.uomOverridden
                ? `UOM overridden (was "${item.originalBillUnit || ''}" @ ${item.originalSellingPrice ?? 0}). Cost and selling cascaded via inventory factors: please review.`
                : 'Change the unit to recalculate cost, selling price and mark-up from the inventory item.'
            }
            className={`${inputCls} ${item.uomOverridden && !lock ? 'border-2 border-emerald-400 bg-emerald-50 font-semibold' : ''}`}
          />
        </Row>
        <Row label="Stock item">
          <StockItemPicker inventoryItemId={item.inventoryItemId || ''} disabled={soft} onChange={(id) => p.updateItem(index, { inventoryItemId: id })} />
        </Row>
        <Row label="Pantry">
          <input aria-label="Qty from Pantry" type="number" min="0" step="0.01" disabled={lock || marked} title={markedTitle} value={item.qtyFromPantry || ''} onChange={(e) => p.onSourceQtyChange('pantry', qty(e.target.value))} className={inputCls} />
        </Row>
        <Row label="Stock">
          <input aria-label="Qty from Stock" type="number" min="0" step="0.01" disabled={lock || marked} title={markedTitle} value={item.qtyFromStock || ''} onChange={(e) => p.onSourceQtyChange('stock', qty(e.target.value))} className={inputCls} />
        </Row>
        {[0, 1, 2].map((slot) => {
          const vq = vendors[slot] || { vendorName: '', quantity: 0 };
          return (
            <Row key={slot} label={`Vendor ${slot + 1}`}>
              <div className="flex gap-2">
                <VendorSelect
                  compact
                  disabled={lock}
                  value={{ vendorId: (vq as { vendorId?: string | null }).vendorId ?? null, name: vq.vendorName || '' }}
                  onChange={(v) => p.updateItem(index, { vendorQuantities: vendorList(slot, { vendorName: v.name, vendorId: v.vendorId }) as ClientInvoiceItemDto['vendorQuantities'] })}
                  placeholder="Vendor name"
                  className="min-w-0 flex-1"
                />
                <input
                  aria-label={`Vendor ${slot + 1} quantity`}
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={lock || marked}
                  title={markedTitle}
                  value={vq.quantity || ''}
                  onChange={(e) => p.onSourceQtyChange(`vendor${slot}` as SourceQtyField, qty(e.target.value))}
                  className="h-8 w-20 rounded border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                />
              </div>
            </Row>
          );
        })}
        {p.notSupplied && (
          <Row label="Not supplied reason">
            <input aria-label="Not supplied reason" disabled={soft} value={item.notSuppliedReason || ''} onChange={(e) => p.updateItem(index, { notSuppliedReason: e.target.value }, true)} placeholder="Why was it not supplied?" className={inputCls} />
          </Row>
        )}
        <Row label="Total billed">
          <span className="font-semibold">{p.totalBilled}</span>
        </Row>
      </Section>

      <Section title="Price">
        <Row label="Unit">
          <span aria-label="Unit" className="inline-block pt-1.5">{invoiceUnit || '-'}</span>
        </Row>
        {/* How many pieces one case holds, from the confirmed bill when there is one, else the PO. A change from the PO's shows
            the PO's struck through (grey) beside the bill's. Nothing is shown when no document states a pack. */}
        {conversion.current && (
          <Row label="Case conversion">
            <span aria-label="Case conversion" className="inline-block pt-1.5">
              {conversion.before && (
                <s aria-label="Case conversion before" title="The pack on the purchase order" className="mr-2 text-slate-400">{conversion.before}</s>
              )}
              <span className="font-medium">{conversion.current}</span>
            </span>
          </Row>
        )}
        {/* D152.2: one box is typed. 'ttd' = TT$ (typable) then US$ (worked out, plain text); 'single' = the one box in the invoice's currency.
            Distinct keys: a layout flip remounts the box, so a half-typed figure is dropped, never saved in the other currency. */}
        {ttdEntry && p.pricing.ttdCost ? (
          <React.Fragment key="ttd-entry">
            <Row label={ttdLabel}>
              {costBefore}
              <DraftInput
                aria-label={ttdLabel}
                inputMode="decimal"
                disabled={soft}
                value={p.pricing.ttdCost.text}
                onCommit={(text) => p.updateTtdCost(index, text)}
                className={`${inputCls} ${overrideCls(soft)}`}
              />
              {useSystemCost}
            </Row>
            <Row label={costLabel}>
              <span aria-label={costLabel} className="inline-block pt-1.5">{p.pricing.costText}</span>
            </Row>
          </React.Fragment>
        ) : (
          <React.Fragment key="single-entry">
            <Row label={costLabel}>
              {!p.pricing.ttdCost && costBefore}
              <DraftInput
                aria-label={costLabel}
                inputMode="decimal"
                disabled={soft}
                value={p.pricing.costText}
                onCommit={(text) => p.updateCostPrice(index, text)}
                className={`${inputCls} ${overrideCls(soft)}`}
              />
              {useSystemCost}
            </Row>
            {p.pricing.ttdCost && (
              <Row label={ttdLabel}>
                {costBefore}
                <DraftInput
                  aria-label={ttdLabel}
                  inputMode="decimal"
                  disabled={soft || p.pricing.ttdCost.disabledReason != null}
                  title={p.pricing.ttdCost.disabledReason ?? undefined}
                  value={p.pricing.ttdCost.text}
                  onCommit={(text) => p.updateTtdCost(index, text)}
                  className={inputCls}
                />
                {p.pricing.ttdCost.disabledReason && <div className="mt-1 text-xs text-amber-700">{p.pricing.ttdCost.disabledReason}</div>}
              </Row>
            )}
          </React.Fragment>
        )}
        {costFrom && (
          <Row label="Cost from">
            <span aria-label="Cost from" className="inline-block pt-1.5">{costFrom}</span>{' '}
            <CostSourceTag item={item} />
          </Row>
        )}
        <Row label="Mark-up %">
          <div className="flex items-center gap-2">
            {/* D152.1: the % sits inside the box; it is not part of the typed value. */}
            <div className="relative min-w-0 flex-1">
              <DraftInput
                aria-label="Mark-up %"
                inputMode="decimal"
                disabled={markupDisabled}
                value={p.pricing.markupText}
                onCommit={(next) => {
                  if (!item.markupOverridden) p.updateItem(index, { markupOverridden: true });
                  p.updateMarkup(index, next);
                }}
                className={`${inputCls} pr-7 ${item.markupOverridden && !markupDisabled ? 'border-2 border-yellow-400 bg-yellow-100 font-semibold' : overrideCls(markupDisabled)}`}
              />
              <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                %
              </span>
            </div>
            {item.markupOverridden && <span className="rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-semibold text-yellow-800">overridden</span>}
          </div>
          {p.pricing.makesMarkupPercent != null && (
            <div className="mt-1 text-xs text-slate-500">makes {p.pricing.makesMarkupPercent.toFixed(2)}% on the line</div>
          )}
        </Row>
        <Row label="Mark-up amount">
          <span>{p.pricing.markupAmount == null || !Number.isFinite(p.pricing.markupAmount) ? '' : p.money(p.pricing.markupAmount, p.currency)}</span>
        </Row>
        <Row label={priceLabel}>
          <DraftInput aria-label={priceLabel} inputMode="decimal" disabled={lock} value={p.pricing.sellingPriceText} onCommit={(text) => p.updateSellingPrice(index, text)} className={`${inputCls} ${overrideCls(lock)}`} />
        </Row>
        <Row label="Line total">
          {p.pricing.lineTotal == null ? (
            <span className="text-xs font-semibold text-amber-700">Unpriced</span>
          ) : (
            <span className="font-semibold">{p.money(p.pricing.lineTotal, p.currency)}</span>
          )}
        </Row>
      </Section>

      {/* D152.3: what the client was quoted, read-only. Only an order line has a quote; the page decides (`quoteInfoFor`). */}
      {p.quoted != null && (
        <Section title="Quote information">
          {p.quoted === 'unavailable' ? (
            <div className="text-xs text-slate-500">Quote details not available</div>
          ) : (
            <>
              <Row label="Unit quoted">
                <span aria-label="Unit quoted" className="inline-block pt-1.5">{p.quoted.unit}</span>
              </Row>
              <Row label="Selling price quoted">
                <span aria-label="Selling price quoted" className="inline-block pt-1.5">{p.quoted.priceText}</span>
              </Row>
              <Row label="Remark in the quote">
                <span aria-label="Remark in the quote" className="inline-block whitespace-pre-wrap pt-1.5">{p.quoted.remark}</span>
              </Row>
            </>
          )}
          {quotedPack}
        </Section>
      )}

      <Section title="Supplier and evidence">
        <Row label="Supplier">
          <VendorSelect
            compact
            disabled={soft}
            value={{ vendorId: item.supplierVendorId ?? null, name: item.supplierName || '' }}
            onChange={(v) => p.updateItem(index, { supplierName: v.name, supplierVendorId: v.vendorId })}
            placeholder={readsAsInventory(item) ? INVENTORY_SUPPLIER : 'Supplier'}
          />
        </Row>
        <Row label="Source">
          <span>
            {SOURCE_LABELS[item.sourceType || 'manual'] || item.sourceType}
            {codes.length > 0 && <span className="text-xs text-slate-500"> ({codes.map((c) => `${c} = ${CODE_MEANING[c]}`).join(', ')})</span>}
          </span>
        </Row>
        <Row label="Came from">
          {provenance.length === 0 ? (
            <span className="text-slate-600">{item.sourceSupplierNames || '-'}</span>
          ) : (
            <div className="space-y-1 text-xs text-slate-600">
              {provenance.map((x, i) => (
                <div key={`${x.kind}-${x.vendorName || ''}-${i}`}>
                  <div>
                    {x.kind === 'stock' ? 'USC Stock' : x.kind === 'pantry' ? 'Pantry' : x.vendorName || 'Unnamed vendor'}: {qty(x.quantity)}
                    {x.poNumber && <span className="text-slate-400"> · PO {x.poNumber}</span>}
                  </div>
                  {(x.vendorInvoices || []).map((vi, j) => (
                    <div key={vi.vendorInvoiceId || j} className="pl-2 text-slate-400">
                      Inv {vi.invoiceNumber || '(no number)'}
                      {vi.matchedBy === 'po_only' && ' · via PO only'}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Row>
        <Row label="PO matches">
          <div className="space-y-1">
            {(item.poMatches || []).length === 0 && <div className="text-slate-500">No match</div>}
            {(item.poMatches || []).map((m, i) => (
              <div key={i} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                <div className="font-medium text-slate-700">{m.poNumber || '--'}</div>
                <div className="text-slate-500">{m.vendorName || '--'}</div>
                <div className="text-slate-500">
                  Qty {p.poMatchQuantity(m)} • {p.money(m.totalPrice, m.currency || p.currency)}
                  {m.priceBasis === 'before_vat' ? ' before VAT' : ''}
                </div>
              </div>
            ))}
          </div>
        </Row>
      </Section>

      <Section title="Delivery and packing list">
        <Row label="Dispatched" hint={DELIVERY_FIELD_NOTES.dispatched}>{qty(item.dispatchedQuantity)}</Row>
        <Row label="Remaining" hint={DELIVERY_FIELD_NOTES.remaining}>{p.remainingDispatch}</Row>
        {p.slipHistory && p.slipHistory.length > 0 && (
          <Row label="Slips" hint={DELIVERY_FIELD_NOTES.slips}>
            <ul aria-label="Delivery slips for this line" className="space-y-0.5 pt-1.5 text-xs text-slate-700">
              {p.slipHistory.map((row, n) => (
                <li key={n}>
                  {`${row.slipNumber ?? row.sequenceLabel ?? 'Slip'}: ${row.quantity} ${row.unit ?? ''}`.trim()}
                  {row.createdAt ? ` · ${formatDate(row.createdAt)}` : ''}
                </li>
              ))}
            </ul>
          </Row>
        )}
        <Row label="Cold storage" hint={DELIVERY_FIELD_NOTES.coldStorage}>
          <button
            type="button"
            aria-pressed={!!item.coldStorageFlag}
            disabled={soft}
            onClick={() => p.updateItem(index, { coldStorageFlag: !item.coldStorageFlag })}
            className={`rounded border px-2 py-1 text-xs ${
              soft ? 'border-slate-300 bg-slate-100 text-slate-500' : item.coldStorageFlag ? 'border-sky-300 bg-sky-100 text-sky-700' : 'border-slate-300 text-slate-600'
            }`}
          >
            {item.coldStorageFlag ? 'Cold storage required' : 'Not cold storage'}
          </button>
        </Row>
      </Section>

      <Section title="Internal - never printed">
        <Row label="Internal remarks">
          <textarea aria-label="Internal remarks" disabled={soft} rows={2} value={item.internalRemarks || ''} onChange={(e) => p.updateItem(index, { internalRemarks: e.target.value }, true)} className={areaCls} />
        </Row>
        <Row label="USCL remarks">
          <textarea aria-label="USCL remarks" disabled={soft} rows={2} value={item.usclRemarks || ''} onChange={(e) => p.updateItem(index, { usclRemarks: e.target.value }, true)} className={areaCls} />
        </Row>
        <Row label="Status colour">
          <select aria-label="Status colour" disabled={soft} value={item.itemStatus || ''} onChange={(e) => p.updateItem(index, { itemStatus: e.target.value as ClientInvoiceItemDto['itemStatus'] })} className={inputCls}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Row>
      </Section>

      <footer className="mt-auto space-y-2 px-4 pb-24 pt-3">
        {writebackOpen && (
          <WritebackForm
            lineNumber={item.lineNumber ?? index + 1}
            costOnly={p.linesLocked}
            busy={p.writebackBusy}
            onCancel={() => setWritebackOpen(false)}
            onSubmit={(a) => {
              p.onWriteback(a);
              setWritebackOpen(false);
            }}
          />
        )}
        <div className="flex items-center justify-between gap-2">
          {p.canWriteback !== false ? (
            <button type="button" disabled={soft || marked} title={markedTitle} onClick={() => setWritebackOpen(true)} className="rounded border border-slate-300 px-3 py-1 text-xs font-medium disabled:opacity-40">
              Record actual pick…
            </button>
          ) : (
            <span />
          )}
          <div className="text-right">
            {marked && (
              <button
                type="button"
                disabled={lock}
                title="Clear the Not supplied reason so the line can be billed again"
                onClick={() => p.updateItem(index, { notSuppliedReason: '' })}
                className="mr-2 rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 disabled:opacity-40"
              >
                Restore line
              </button>
            )}
            <button
              type="button"
              disabled={lock || p.removalLock != null}
              onClick={() => p.removeItem(index)}
              className="rounded border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 disabled:opacity-40"
            >
              Remove line
            </button>
            {!p.readOnly && (p.linesLocked || p.removalLock) && (
              <div role="note" className="mt-1 text-xs text-slate-500">
                {p.linesLocked ? 'Locked once sent' : p.removalLock}
              </div>
            )}
          </div>
        </div>
      </footer>
    </aside>
  );
}
